import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/common/StatusChip";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const ClientSummaryDialog = ({ open, onOpenChange, property }) => {
  const { t } = useT();
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["client-summary", property?.id],
    enabled: open && !!property?.id,
    queryFn: async () => (await api.get(`/client-summary/${property.id}/preview`)).data,
  });

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/client-summary/${property.id}/send`);
      toast.success(t("summary.sent"));
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-summary-dialog" className="max-h-[92vh] max-w-[620px] overflow-y-auto rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("summary.title")}</DialogTitle>
          <DialogDescription className="text-[13px]">{t("summary.desc")}</DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <p className="py-6 text-[13px] text-ink-tertiary">{t("app.loading")}</p>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <StatusChip tone="blue" testId="summary-channel" label={t(`channels.${data.destinatario.canal}`)} />
              <span className="text-[12.5px] text-ink-secondary">
                {data.destinatario.nombre} ·{" "}
                {data.destinatario.canal === "email" ? data.destinatario.email : data.destinatario.telefono}
              </span>
              <StatusChip tone="gray" label={t("summary.days", { n: data.dias })} />
            </div>

            <div className="rounded-[8px] border border-line bg-white">
              <header className="flex items-center gap-2 border-b border-line bg-surface-sunken px-3 py-2">
                <Mail className="h-3.5 w-3.5 text-ink-tertiary" />
                <p data-testid="summary-subject" className="text-[12.5px] font-semibold text-ink">
                  {data.asunto || property?.nombre}
                </p>
              </header>
              <pre data-testid="summary-body" className="whitespace-pre-wrap px-3 py-3 font-sans text-[12.5px] leading-relaxed text-ink">
{data.cuerpo || data.resumen}
              </pre>
            </div>
            <p className="text-[11.5px] text-ink-tertiary">{t("summary.no_amounts")}</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-[6px] text-[13px]">
            {t("app.cancel")}
          </Button>
          <Button data-testid="summary-send" disabled={busy || isLoading} onClick={send}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
            {t("summary.send_now")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
